import { Fragment, useState, useRef, useEffect } from 'react';
import { Dialog, Transition, RadioGroup } from '@headlessui/react';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../api/client';
import { useNotificationDispatch } from '../../context/NotificationContext';

// --- Ícones ---
const SpinnerIcon = () => <svg className="animate-spin h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>;
const CheckIconSmall = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>;
const CopyIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>;

const presetAmounts = [20.00, 50.00, 100.00, 200.00];

interface AddFundsModalProps {
    isOpen: boolean;
    closeModal: () => void;
    onSuccess: () => void;
}

export function AddFundsModal({ isOpen, closeModal, onSuccess }: AddFundsModalProps) {
    const { user } = useAuth();
    const { showNotification } = useNotificationDispatch();
    const [amount, setAmount] = useState<number>(presetAmounts[0]);
    const [status, setStatus] = useState<'idle' | 'processing' | 'awaiting_pix' | 'error'>('idle');
    const [pixData, setPixData] = useState<{ paymentId: string; qrCode: string; qrCodeBase64: string } | null>(null);
    const [error, setError] = useState('');
    const [isCopied, setIsCopied] = useState(false);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const handleGeneratePix = async () => {
        if (amount <= 0 || isNaN(amount)) {
            setError('O valor deve ser maior que R$ 0,00.');
            return;
        }

        setError('');
        setStatus('processing');
        setPixData(null);

        try {
            const response = await apiClient.post('/checkout/process-payment', {
                paymentType: 'add_funds',
                actionAmount: amount,
                payer: { email: user?.email }
            });
            const result = response.data;

            setPixData(result);
            setStatus('awaiting_pix');

            if (intervalRef.current) clearInterval(intervalRef.current);
            intervalRef.current = setInterval(() => checkPixStatus(result.paymentId), 5000);

        } catch (err: any) {
            const errorMsg = err.response?.data?.error || 'Falha ao gerar o pagamento PIX.';
            setError(errorMsg);
            setStatus('error');
        }
    };

    const checkPixStatus = async (paymentId: string) => {
        try {
            const response = await apiClient.post('/checkout/payment-status', { paymentId });
            const result = response.data;

            if (result.status === 'approved') {
                if (intervalRef.current) clearInterval(intervalRef.current);
                setStatus('idle');
                onSuccess(); // Dispara o refreshUser no AuthContext para atualizar o saldo
                closeModal();
                showNotification(`Depósito de R$ ${amount.toFixed(2).replace('.', ',')} aprovado!`, 'success');
            } else if (['cancelled', 'rejected'].includes(result.status)) {
                if (intervalRef.current) clearInterval(intervalRef.current);
                setError('O PIX expirou ou foi cancelado.');
                setStatus('error');
            }
        } catch (error) {
            console.error("Erro ao verificar status do PIX", error);
        }
    };

    useEffect(() => {
        return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    }, []);

    const handleClose = () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        setStatus('idle');
        setPixData(null);
        setError('');
        setAmount(presetAmounts[0]);
        closeModal();
    }

    const handleCopyPixCode = () => {
        if (!pixData) return;
        navigator.clipboard.writeText(pixData.qrCode);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={handleClose}>
                <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-black bg-opacity-40" />
                </Transition.Child>
                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0 scale-95">
                            <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                <Dialog.Title as="h3" className="text-lg font-bold leading-6 text-gray-900 border-b pb-3">
                                    Adicionar Fundos à Carteira
                                </Dialog.Title>
                                {error && <p className="text-red-500 text-sm mt-3">{error}</p>}

                                {status === 'idle' && (
                                    <>
                                        <div className="mt-4">
                                            <label className="form-label">Selecione o Valor (Apenas PIX disponível)</label>
                                            <RadioGroup value={amount} onChange={setAmount} className="mt-2">
                                                <div className="grid grid-cols-2 gap-3">
                                                    {presetAmounts.map((preset) => (
                                                        <RadioGroup.Option key={preset} value={preset} className={({ checked }) => `${checked ? 'bg-brand-blue text-white' : 'bg-gray-100 text-text-primary'} relative flex cursor-pointer rounded-lg px-4 py-3 shadow-sm focus:outline-none transition-colors`}>
                                                            <span className="font-semibold">R$ {preset.toFixed(2).replace('.', ',')}</span>
                                                        </RadioGroup.Option>
                                                    ))}
                                                </div>
                                            </RadioGroup>
                                            <input type="number" value={amount} onChange={(e) => setAmount(parseFloat(e.target.value) || 0)} step="0.01" min="1.00" placeholder="Ou digite um valor" className="form-input mt-4" />
                                        </div>
                                        <div className="mt-6 flex justify-end gap-3 border-t pt-4">
                                            <button type="button" onClick={handleClose} className="px-5 py-2.5 text-sm font-semibold text-text-secondary bg-gray-100 rounded-lg hover:bg-gray-200">Cancelar</button>
                                            <button type="button" onClick={handleGeneratePix} className="px-6 py-2.5 text-sm font-semibold text-white bg-green-500 rounded-lg hover:bg-green-600 disabled:bg-gray-400">Gerar PIX</button>
                                        </div>
                                    </>
                                )}

                                {(status === 'processing' || status === 'awaiting_pix') && (
                                    <div className="text-center mt-6">
                                        <h3 className="font-bold text-lg text-gray-800">{status === 'processing' ? 'Gerando PIX...' : `Pagar R$ ${amount.toFixed(2).replace('.', ',')}`}</h3>
                                        {status === 'processing' && <div className="flex items-center justify-center mt-4 text-brand-blue"><SpinnerIcon /></div>}
                                        {status === 'awaiting_pix' && pixData && (
                                            <div className="mt-4">
                                                <p className="text-sm text-gray-500 mb-3">Escaneie o QR Code abaixo com o app do seu banco.</p>
                                                <div className="bg-white p-2 border rounded-lg shadow-sm inline-block"><img src={`data:image/jpeg;base64,${pixData.qrCodeBase64}`} alt="PIX QR Code" className="w-64 h-64 mx-auto" /></div>
                                                <div className="mt-6 max-w-md mx-auto text-center">
                                                    <div className="relative">
                                                        <input readOnly value={pixData.qrCode} className="w-full bg-gray-200 border-gray-300 rounded-lg p-3 pr-28 text-sm text-gray-700 font-mono focus:ring-0 focus:border-gray-300" />
                                                        <button onClick={handleCopyPixCode} className={`absolute inset-y-0 right-0 flex items-center px-4 rounded-r-lg transition-colors ${isCopied ? 'bg-green-500 text-white' : 'bg-gray-300 hover:bg-gray-400 text-gray-800'}`}>
                                                            {isCopied ? <CheckIconSmall /> : <CopyIcon />}
                                                            <span className="ml-2 text-sm font-semibold">{isCopied ? 'Copiado!' : 'Copiar'}</span>
                                                        </button>
                                                    </div>
                                                    <p className="mt-4 text-brand-blue font-semibold animate-pulse">Aguardando pagamento...</p>
                                                </div>
                                            </div>
                                        )}
                                        <button onClick={handleClose} className="mt-6 text-sm text-text-secondary hover:underline">Fechar e Cancelar</button>
                                    </div>
                                )}
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}